import { Grid, Popover, Stack, TextField, Typography } from "@mui/material";
import { ProgressButton } from "@qubit/autoparts";
import { MouseEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Props = {
  onScanBin: (binNumber: number) => Promise<boolean>;
};

export function BinScanInput({ onScanBin }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [isBinNumberEmptyErrorMessage, setIsBinNumberEmptyErrorMessage] =
    useState<string | null>(null);
  const enteredBinNumber = useRef("");

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleClose = () => {
    enteredBinNumber.current = "";
    setAnchorEl(null);
    setOpen(false);
    setIsBinNumberEmptyErrorMessage(null);
  };

  const handleBinNumberChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    enteredBinNumber.current = event.target.value;
    if (enteredBinNumber.current.length && isBinNumberEmptyErrorMessage) {
      setIsBinNumberEmptyErrorMessage(null);
    }
  };

  const handleConfirm = async () => {
    const trimmedBinNumber = enteredBinNumber.current.trim();
    if (!trimmedBinNumber.length) {
      setIsBinNumberEmptyErrorMessage("* Required");
      return;
    }

    const parsedBinNumber = Number(trimmedBinNumber);
    if (!Number.isFinite(parsedBinNumber) || parsedBinNumber <= 0) {
      setIsBinNumberEmptyErrorMessage("* Invalid bin number");
      return;
    }

    const success = await onScanBin(parsedBinNumber);
    if (success) {
      handleClose();
    }
  };

  return (
    <Stack spacing={0.5} alignSelf="stretch" sx={{ width: "100%" }}>
      <Typography
        variant="caption"
        fontWeight={700}
        sx={{ color: "warning.main", textTransform: "uppercase" }}
      >
        {t("dev mode")}:
      </Typography>
      <ProgressButton
        onClick={handleClick}
        responsive
        variant="contained"
        color="secondary"
        size="large"
        fullWidth
      >
        {t("enter bin number")}
      </ProgressButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left"
        }}
        disableScrollLock
      >
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item>
            <TextField
              error={!!isBinNumberEmptyErrorMessage}
              inputRef={(input: HTMLInputElement) => input && input.focus()}
              onChange={handleBinNumberChange}
              inputProps={{ style: { fontSize: "20px" } }}
              InputProps={{
                endAdornment: (
                  <ProgressButton
                    onClick={handleConfirm}
                    variant="contained"
                    color="primary"
                    size="large"
                  >
                    {t("confirm")}
                  </ProgressButton>
                )
              }}
              helperText={isBinNumberEmptyErrorMessage}
              FormHelperTextProps={{ style: { fontSize: "20px" } }}
            />
          </Grid>
        </Grid>
      </Popover>
    </Stack>
  );
}
